/**
 * Header search — presentational sizing and scrolling for the suggestions plate.
 *
 * This file adds NO search behaviour. predictive-search.js still owns the
 * query, the results markup, the combobox ARIA and the live region;
 * search-form.js still owns the reset button; details-modal.js still owns
 * open/close, the focus trap and Escape. Nothing here calls into any of them.
 *
 * It exists for two things the expanding plate needs and CSS cannot express:
 *
 * 1. THE KEYBOARD-OVERLAP FIX. predictive-search.js caps the dropdown with
 *    `window.innerHeight - <header bottom>` (getResultsMaxHeight). innerHeight
 *    is the LAYOUT viewport, and on iOS Safari the on-screen keyboard does not
 *    resize the layout viewport — so on a phone the panel is sized as if the
 *    keyboard were not there and its lower half sits underneath the keys.
 *    The fix is to measure the VISUAL viewport instead, which is the box that
 *    shrinks, and let the remainder scroll inside the panel. `dvh` is not a
 *    substitute for the same reason.
 *
 * 2. SCROLLING THE SELECTED OPTION INTO VIEW. The result rows are
 *    role="option" tabindex="-1" and predictive-search.js never focuses them —
 *    it drives aria-activedescendant and leaves DOM focus in the combobox — so
 *    the browser has nothing to scroll. That is survivable while the panel is
 *    tall; it is not once the panel is capped to a keyboard-shrunk viewport,
 *    where arrow-down would walk the selection off the bottom with no visible
 *    change. This is what makes the stylesheet's `scroll-padding-block-end`
 *    load-bearing rather than decorative.
 *
 * Both write only to the same inline `max-height` / scroll position that
 * predictive-search.js already writes and clears, so there is no second source
 * of truth to fall out of sync.
 */
(function () {
  'use strict';

  /** Never squeeze the panel below this, however little room there is. */
  var MIN_PANEL_HEIGHT = 120;
  /** Breathing room between the panel's bottom edge and the keyboard. */
  var VIEWPORT_GAP = 12;

  function headerInstances() {
    return document.querySelectorAll('.header__search predictive-search');
  }

  /**
   * Cap one instance's results panel to the visual viewport.
   * Leaves the inline style alone unless the panel is actually showing, so a
   * closed instance never carries a stale height into its next open.
   */
  function capPanel(instance) {
    var panel = instance.querySelector('[data-predictive-search]');
    if (!panel) return;

    if (!instance.hasAttribute('open') && !instance.hasAttribute('loading')) return;

    var viewport = window.visualViewport;
    var viewportTop = viewport ? viewport.offsetTop : 0;
    var viewportBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
    var panelTop = panel.getBoundingClientRect().top;

    // A cap against the viewport is only meaningful while the panel is IN it.
    // In a sticky header it always is, but a header scrolled off the top would
    // otherwise compute a negative height and clamp to the floor.
    if (panelTop < viewportTop || panelTop > viewportBottom - MIN_PANEL_HEIGHT) return;

    var available = Math.round(viewportBottom - panelTop - VIEWPORT_GAP);
    panel.style.maxHeight = Math.max(MIN_PANEL_HEIGHT, available) + 'px';
  }

  function capAll() {
    headerInstances().forEach(capPanel);
  }

  /**
   * Keep the aria-activedescendant row visible inside the capped panel.
   * `block: 'nearest'` is what makes this a no-op when the row is already
   * on screen, and it honours the stylesheet's scroll-padding.
   */
  function revealActiveOption(instance) {
    var input = instance.querySelector('input[type="search"]');
    if (!input) return;

    var id = input.getAttribute('aria-activedescendant');
    if (!id) return;

    var option = instance.querySelector('#' + CSS.escape(id));
    if (!option) return;

    option.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  function observe(instance) {
    // predictive-search.js signals every state change we care about through
    // these two attributes on the custom element, and through
    // aria-activedescendant on the input.
    new MutationObserver(function () {
      capPanel(instance);
    }).observe(instance, { attributes: true, attributeFilter: ['open', 'loading'] });

    var input = instance.querySelector('input[type="search"]');
    if (!input) return;

    new MutationObserver(function () {
      revealActiveOption(instance);
    }).observe(input, { attributes: true, attributeFilter: ['aria-activedescendant'] });
  }

  function init() {
    var instances = headerInstances();
    if (!instances.length) return;

    instances.forEach(observe);

    if (window.visualViewport) {
      // The keyboard opening IS a visual-viewport resize on a real device.
      window.visualViewport.addEventListener('resize', capAll);
      window.visualViewport.addEventListener('scroll', capAll);
    }
    window.addEventListener('resize', capAll);

    capAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
